'use client';

import { GroqImage } from '@/helpers/Image';
import type { PageRizAffiliateSchema } from '@/sanity/pages/PageRizAffiliateTemplate';
import { measuredRichText, V3_HIGHLIGHT } from './richtext';
import ChevronCta from './ChevronCta';
import AboutPhoto from './about/AboutPhoto';
import AboutStats from './about/AboutStats';
import { RevealBlock, RevealEyebrow, RevealRichText, RevealStyles } from './about/reveal';

export default function AboutSection({ data }: { data: PageRizAffiliateSchema }) {
  if (
    !data?.aboutChip &&
    !data?.aboutHeading?.length &&
    !data?.aboutText?.length &&
    !data?.aboutCTA &&
    !data?.aboutImage &&
    !data?.aboutStats?.length
  )
    return null;

  return (
    <section className="bg-white px-[2rem] py-[8rem] lg:px-0 lg:py-[15rem]">
      <RevealStyles />
      <div className="flex flex-col mx-auto w-[min(150rem,100%)] gap-[3rem] lg:gap-[5rem]">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="max-lg:contents lg:flex lg:flex-col lg:w-[min(64rem,100%)]">
            {(data?.aboutChip || data?.aboutHeading?.length > 0) && (
              <div className="flex flex-col items-center gap-[2rem] lg:items-start">
                <RevealEyebrow text={data?.aboutChip} />
                <RevealRichText
                  value={data?.aboutHeading}
                  components={measuredRichText}
                  className={`${V3_HIGHLIGHT} richtext richtext-center w-[min(35.25rem,100%)] max-lg:[&>h2:first-child]:w-[min(29rem,100%)] max-lg:[&>h2:first-child]:[text-wrap:wrap] lg:w-full lg:richtext-start [&_span]:!text-black prose-headings:text-black prose-headings:font-geist prose-headings:font-[500] prose-headings:text-[4.5rem] prose-headings:leading-none prose-headings:tracking-[-0.225rem] lg:prose-headings:text-[6rem] lg:prose-headings:tracking-[-0.3rem] [--hl-ctr:0.1834rem] [--hl-pl:0.287rem] [--hl-pr:0.928rem] [--hl-ext:0rem] [--hl-wash:4.5rem] [--hl-dy:1.05rem] lg:[--hl-ext:0rem] lg:[--hl-wash:6.75rem] lg:[--hl-dy:0.125rem] lg:[--hl-ctr:0px] lg:[--hl-pl:0.75rem] lg:[--hl-pr:1.15rem] lg:[&_.hl-tick-r]:![top:var(--hl-top)] lg:[&_.hl-tick-l]:![top:var(--hl-top)] lg:[&_.hl-tick-l>span:first-child]:![height:calc(var(--hl-w)-0.5rem)]`}
                />
              </div>
            )}

            <RevealRichText
              value={data?.aboutText}
              components={measuredRichText}
              mode="text"
              className="order-3 w-[min(48.625rem,100%)] richtext richtext-start [text-wrap:wrap] max-lg:[&_br]:hidden prose-p:m-0 prose-p:font-geist prose-p:font-[400] prose-p:text-[2.25rem] prose-p:leading-[1.5] prose-p:text-black/[0.6] [&_p+p]:mt-[3.375rem] lg:mt-[3.625rem] lg:w-[min(60.75rem,100%)] lg:prose-p:leading-[1.33] lg:prose-p:text-black/[0.7] lg:[&_p+p]:mt-[3rem]"
            />

            {(data?.aboutCTA || data?.aboutSignature) && (
              <RevealBlock className="order-3 flex flex-col-reverse items-start w-full gap-[3rem] lg:flex-col lg:mt-[4.5rem] lg:gap-0">
                {data?.aboutCTA && (
                  <ChevronCta
                    wide
                    data={data.aboutCTA}
                    language={data?.settings?.language}
                  />
                )}
                {data?.aboutSignature && (
                  <div className="flex items-center justify-center pointer-events-none ml-[1.375rem] w-[14rem] h-[11rem] lg:ml-0 lg:mt-[3rem] lg:pl-[0.75rem] lg:pb-[1rem] lg:h-[5.75rem]">
                    <GroqImage
                      src={data.aboutSignature}
                      alt={data.aboutSignature?.name ?? ''}
                      quality={100}
                      sizes="(min-width: 993px) 7vw, 31vw"
                      style={{ width: '16.625rem' }}
                      className="shrink-0 max-w-none !h-auto"
                    />
                  </div>
                )}
              </RevealBlock>
            )}
          </div>

          <AboutPhoto
            data={data}
            className="order-2 mt-[4.5rem] mb-[3rem] w-full lg:order-none lg:m-0 lg:w-[79.25rem]"
          />
        </div>

        <AboutStats data={data} />
      </div>
    </section>
  );
}
